import { useState } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { OrganizationForm, type IOrganizationForm } from 'src/components/Organization';
import { type IEscrowPartyParams } from 'src/graphql/schema';
import { BeneficiaryUpdateDocument } from 'src/graphql/actions/beneficiary/beneficiaryUpdate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { updateBeneficiaryCache } from 'src/graphql/client/cache';
import { OrganizationRegistrationNumberTooltip } from '@/components/Organization';

import { useBeneficiaryContext } from '../model';

const EditBeneficiaryDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { beneficiary, escrowId } = useBeneficiaryContext();
  const [updateBeneficiary] = useMutation(BeneficiaryUpdateDocument, {
    update: (_, { data }) => updateBeneficiaryCache(escrowId, data?.beneficiaryUpdate?.beneficiary),
  });

  const onSubmit = async ({
    companyName,
    street,
    streetNumber,
    city,
    postalCode,
    region,
    country,
    companyWebsite,
    companyRegistration,
  }: IOrganizationForm) => {
    const companyVariables: IEscrowPartyParams = {
      companyName,
      street,
      streetNumber,
      city,
      postalCode,
      region,
      country,
      companyWebsite,
      companyRegistrationNumber: companyRegistration,
    };
    try {
      const { data } = await updateBeneficiary({
        variables: {
          beneficiaryId: beneficiary?.id ?? '',
          beneficiaryParams: companyVariables,
        },
      });

      if (data?.beneficiaryUpdate?.errors?.length) {
        throw new ClientError(data?.beneficiaryUpdate?.errors[0]);
      } else {
        toast.success('Details updated!');
        setIsOpen(false);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const closeDialog = () => setIsOpen(false);

  const organizationDetails: IOrganizationForm = {
    street: beneficiary?.street || '',
    city: beneficiary?.city || '',
    postalCode: beneficiary?.postalCode || '',
    region: beneficiary?.region || '',
    country: beneficiary?.country || '',
    companyName: beneficiary?.companyName || '',
    companyWebsite: beneficiary?.companyWebsite || '',
    companyRegistration: beneficiary?.companyRegistrationNumber || '',
    streetNumber: beneficiary?.streetNumber || '',
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Edit</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[976px] gap-0">
        <DialogHeader className="mb-6">
          <DialogTitle className="text-base font-bold">Enter organization details</DialogTitle>
          <DialogDescription className="text-xs">
            Enter the details of the organization. This includes the registered name and address of the entity, which
            will be listed in the escrow agreement.
          </DialogDescription>
        </DialogHeader>
        <OrganizationForm
          onSubmit={onSubmit}
          onCancel={closeDialog}
          defaultValues={organizationDetails}
          registrationTooltip={<OrganizationRegistrationNumberTooltip />}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditBeneficiaryDialog;
